import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownModal = ({ isOpen, onClose, markdownUrl }) => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && markdownUrl) {
      setIsLoading(true);
      setError(null);
      fetch(markdownUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to load content');
          }
          return response.text();
        })
        .then(text => {
          setContent(text);
          setIsLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setIsLoading(false);
        });
    }
  }, [isOpen, markdownUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-950 w-full max-w-3xl rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900"
        >
          <X size={24} />
        </button>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6 pt-8">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500 dark:text-gray-400">Loading...</div>
            </div>
          )}
          {error && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-red-600 dark:text-red-400 text-sm p-4 rounded-xl">
              {error}
            </div>
          )}
          {!isLoading && !error && content && (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ node, children, ...props }) => (
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-4 mt-6" {...props}>{children}</h1>
                  ),
                  h2: ({ node, children, ...props }) => (
                    <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-3 mt-5" {...props}>{children}</h2>
                  ),
                  h3: ({ node, children, ...props }) => (
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 mt-4" {...props}>{children}</h3>
                  ),
                  p: ({ node, ...props }) => (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 leading-relaxed" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul className="list-disc list-outside text-sm text-gray-700 dark:text-gray-300 mb-3 space-y-2 ml-6" {...props} />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol className="list-decimal list-outside text-sm text-gray-700 dark:text-gray-300 mb-3 space-y-2 ml-6" {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed" {...props} />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong className="font-bold text-gray-900 dark:text-white" {...props} />
                  ),
                  a: ({ node, children, ...props }) => (
                    <a className="text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300 underline" {...props}>{children}</a>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50/50 dark:bg-gray-900/50 border-t border-gray-50 dark:border-gray-800 shrink-0">
          <button
            onClick={onClose}
            className="w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkdownModal;
